import React, { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import { sliderService } from '../services/api'

const ModernSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [touchStartX, setTouchStartX] = useState(0)
  const [touchEndX, setTouchEndX] = useState(0)
  const autoAdvanceTimer = useRef(null)
  
  const { data: slides, isLoading } = useQuery({
    queryKey: ['sliderImages'],
    queryFn: sliderService.getSlides,
    retry: false,
  })

  const activeSlides = slides?.filter(slide => slide.is_active) || []

  useEffect(() => {
    if (activeSlides.length > 1) {
      resetAutoAdvance()
    }
    return () => {
      if (autoAdvanceTimer.current) {
        clearInterval(autoAdvanceTimer.current)
      }
    }
  }, [activeSlides.length])

  const resetAutoAdvance = () => {
    if (autoAdvanceTimer.current) {
      clearInterval(autoAdvanceTimer.current)
    }
    autoAdvanceTimer.current = setInterval(() => {
      nextSlide()
    }, 5000)
  }

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % activeSlides.length)
    resetAutoAdvance()
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + activeSlides.length) % activeSlides.length)
    resetAutoAdvance()
  }

  const goToSlide = (index) => {
    setCurrentSlide(index)
    resetAutoAdvance()
  }

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX)
  }

  const handleTouchEnd = (e) => {
    setTouchEndX(e.changedTouches[0].clientX)
    handleSwipe()
  }

  const handleSwipe = () => {
    if (!touchStartX || !touchEndX) return
    
    const difference = touchStartX - touchEndX
    const isLeftSwipe = difference > 50
    const isRightSwipe = difference < -50

    if (isLeftSwipe && activeSlides.length > 1) {
      nextSlide()
    }
    if (isRightSwipe && activeSlides.length > 1) {
      prevSlide()
    }
  }

  const getSlidePosition = (index) => {
    const position = index - currentSlide
    const totalSlides = activeSlides.length
    
    if (position === 0) return 'active'
    if (position === 1 || (position === -(totalSlides - 1))) return 'next'
    if (position === -1 || (position === totalSlides - 1)) return 'prev'
    return 'hidden'
  }

  const getSlideTransform = (position) => {
    switch (position) {
      case 'active':
        return 'scale(1) translateZ(0px)'
      case 'next':
        return 'scale(0.9) translateX(100%) translateZ(-100px)'
      case 'prev':
        return 'scale(0.9) translateX(-100%) translateZ(-100px)'
      default:
        return 'scale(0.8) translateZ(-200px)'
    }
  }

  const getSlideOpacity = (position) => {
    switch (position) {
      case 'active':
        return 1
      case 'next':
      case 'prev':
        return 0.7
      default:
        return 0
    }
  }

  if (isLoading) {
    return (
      <div className="relative w-full h-[400px] sm:h-[500px] md:h-[600px] bg-gray-100 rounded-xl animate-pulse flex items-center justify-center">
        <div className="text-gray-400">Loading slider...</div>
      </div>
    )
  }

  if (!activeSlides.length) {
    return null
  }

  return (
    <div className="relative w-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-white">
      {/* Background decoration */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100/20 via-gray-50/20 to-gray-100/20"></div>
        <div className="absolute top-1/4 left-1/4 w-48 h-48 sm:w-96 sm:h-96 bg-gray-200/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 sm:w-96 sm:h-96 bg-gray-200/10 rounded-full filter blur-3xl"></div>
      </div>

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gray-300/30 rounded-full overflow-hidden z-20">
        <div 
          className="absolute top-0 left-0 h-full transition-all duration-500 ease-out"
          style={{ 
            width: `${((currentSlide + 1) / activeSlides.length) * 100}%`,
            background: 'var(--color-gold, #c0b283)'
          }}
        ></div>
      </div>

      {/* Navigation buttons */}
      {activeSlides.length > 1 && (
        <>
          <button 
            onClick={prevSlide}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center z-20 transition-all duration-300 bg-gray-800/10 backdrop-blur-sm hover:bg-gray-800/20 hover:scale-110 active:scale-95"
            style={{ color: 'var(--color-gold, #c0b283)' }}
            title="Previous slide"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          
          <button 
            onClick={nextSlide}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center z-20 transition-all duration-300 bg-gray-800/10 backdrop-blur-sm hover:bg-gray-800/20 hover:scale-110 active:scale-95"
            style={{ color: 'var(--color-gold, #c0b283)' }}
            title="Next slide"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </>
      )}

      {/* Carousel track */}
      <div 
        className="relative h-[400px] sm:h-[500px] md:h-[600px] overflow-hidden"
        style={{ 
          perspective: '1000px',
          touchAction: 'pan-y pinch-zoom'
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          className="relative w-full h-full"
          style={{ transformStyle: 'preserve-3d' }}
        >
          {activeSlides.map((slide, index) => {
            const position = getSlidePosition(index)
            
            const SlideContent = ({ children }) => {
              if (slide.link_url && slide.link_url.trim()) {
                return (
                  <a
                    href={slide.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full h-full cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {children}
                  </a>
                )
              }
              return <div className="w-full h-full">{children}</div>
            }

            return (
              <div
                key={slide.id}
                className={`absolute top-0 left-0 w-full h-full transition-all duration-500 ease-out ${position}`}
                style={{
                  backfaceVisibility: 'hidden',
                  opacity: getSlideOpacity(position),
                  transform: getSlideTransform(position)
                }}
              >
                <div className="w-full h-full p-4 sm:p-8">
                  <SlideContent>
                    <div className={`w-full h-full rounded-xl sm:rounded-2xl overflow-hidden relative group ${slide.link_url && slide.link_url.trim() ? 'hover:shadow-2xl transition-shadow duration-300' : ''}`}>
                      <img 
                        src={`/uploads/slider/${slide.image}`} 
                        alt={slide.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      
                      {/* Overlay with brand color */}
                      <div 
                        className="absolute inset-0 mix-blend-overlay"
                        style={{ 
                          background: `linear-gradient(135deg, rgba(192, 178, 131, 0.4) 0%, rgba(64, 74, 66, 0.4) 100%)`
                        }}
                      ></div>
                      
                      {/* Clickable indicator */}
                      {slide.link_url && slide.link_url.trim() && (
                        <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-full p-2 opacity-75 group-hover:opacity-100 transition-opacity duration-300">
                          <ExternalLink className="w-4 h-4 text-white" />
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="absolute inset-x-0 bottom-0 p-4 sm:p-8 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                        <h3 className="text-white text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3">
                          {slide.title}
                        </h3>
                        <p className="text-gray-200 text-sm sm:text-base md:text-lg max-w-2xl">
                          {slide.description}
                        </p>
                        {slide.link_url && slide.link_url.trim() && (
                          <div className="mt-4">
                            <span className="inline-flex items-center px-4 py-2 backdrop-blur-sm rounded-lg text-white/90 text-sm font-medium pointer-events-none">
                              <span className="mr-2">انقر للمزيد</span>
                              <ExternalLink className="w-4 h-4" />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </SlideContent>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Indicators with gold color */}
      {activeSlides.length > 1 && (
        <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 flex gap-1 sm:gap-2 z-20">
          {activeSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className="w-8 sm:w-12 h-1 sm:h-1.5 rounded-full transition-all duration-300 hover:scale-110"
              style={{
                backgroundColor: index === currentSlide 
                  ? 'var(--color-gold, #c0b283)' 
                  : 'rgba(192, 178, 131, 0.3)',
                opacity: index === currentSlide ? 1 : 0.6
              }}
              title={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default ModernSlider
